import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, Sparkles, ExternalLink } from 'lucide-react';
import { getAdminEvents, deleteEvent, createEvent } from '../../services/api';
import { FALLBACK_EVENTS, eventSlug } from '../../utils/eventData';

const AdminEvents = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    getAdminEvents()
      .then((r) => setItems(r.data || []))
      .catch(() => setError('Etkinlikler yüklenemedi'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const importTemplate = async () => {
    setBusy(true);
    setError('');
    try {
      const tpl = FALLBACK_EVENTS[0];
      await createEvent({ ...tpl, slug: eventSlug(tpl), active: true });
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'İçe aktarılamadı');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`"${name}" etkinliğini silmek istediğinize emin misiniz?`)) return;
    await deleteEvent(id);
    load();
  };

  return (
    <div data-testid="admin-events-page">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-[#1B1B1B]" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            Özel Etkinlikler
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Etkinlik sayfalarının tüm metinleri, programı ve görselleri buradan düzenlenir.
          </p>
        </div>
        <Link
          to="/admin/etkinlikler/yeni"
          data-testid="admin-events-new"
          className="inline-flex items-center gap-2 bg-[#3D4A26] text-white font-bold text-sm px-5 py-2.5 rounded-lg hover:bg-[#2d3a1a]"
        >
          <Plus size={16} /> Yeni Etkinlik
        </Link>
      </div>

      {error && <p className="mb-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}

      {loading ? (
        <p className="text-gray-500">Yükleniyor...</p>
      ) : items.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center" data-testid="admin-events-empty">
          <Sparkles size={26} className="text-[#3D4A26] mx-auto mb-3" />
          <p className="text-sm text-gray-600 mb-1">Henüz panele kayıtlı etkinlik yok.</p>
          <p className="text-xs text-gray-400 mb-5">
            Sitede şu an Likya Yolu 49W etkinliği hazır içerikle yayında. İçe aktarırsanız panelden düzenlenebilir hale gelir.
          </p>
          <button
            type="button"
            onClick={importTemplate}
            disabled={busy}
            data-testid="admin-events-import"
            className="inline-flex items-center gap-2 bg-[#C8D843] text-[#1B1B1B] font-bold text-sm px-5 py-2.5 rounded-lg hover:bg-[#b8c83a] disabled:opacity-60"
          >
            {busy ? 'İçe aktarılıyor...' : 'Likya 49W etkinliğini içe aktar'}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((ev) => (
            <div
              key={ev.id}
              data-testid={`admin-event-row-${ev.id}`}
              className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4"
            >
              <img
                src={ev.heroImage || ev.heroImageFallback}
                alt=""
                className="w-20 h-16 object-cover rounded-lg bg-gray-100 flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-bold text-sm text-[#1B1B1B] truncate">{ev.name}</p>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                    ev.active ? 'bg-[#C8D843] text-[#1B1B1B]' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {ev.active ? 'Yayında' : 'Taslak'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {ev.dateLabel} · {ev.capacity} kişi · /{eventSlug(ev)}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <a
                  href={`/${eventSlug(ev)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-gray-400 hover:text-[#3D4A26]"
                  title="Sayfayı gör"
                >
                  <ExternalLink size={16} />
                </a>
                <Link
                  to={`/admin/etkinlikler/${ev.id}/duzenle`}
                  data-testid={`admin-event-edit-${ev.id}`}
                  className="p-2 text-gray-400 hover:text-[#3D4A26]"
                  title="Düzenle"
                >
                  <Pencil size={16} />
                </Link>
                <button
                  type="button"
                  onClick={() => remove(ev.id, ev.name)}
                  data-testid={`admin-event-delete-${ev.id}`}
                  className="p-2 text-gray-400 hover:text-red-500"
                  title="Sil"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminEvents;
